import React, { useState, useRef, useEffect, useCallback, useMemo, useContext, useReducer } from 'react';
import { MapContainer, TileLayer, useMap, GeoJSON, useMapEvents } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import 'leaflet-geometryutil';
import './Map.css';
import { useNavigate, useSearchParams } from 'react-router-dom'; // Import useLocation and useNavigate
import { UserContext } from '../context/UserContext'; // Import UserContext
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../hooks/useLanguage';
import { NotificationContext } from '../context/NotificationContext'; // Import NotificationContext
import { useMapContext } from '../context/MapContext'; // Import useMapContext
import { PropertyTypeNumericToStringMap } from '../utils/enums';
import { fetchWithTimeout } from '../utils/fetchWithTimeout';
import MapSidebar from '../components/map/MapSidebar';

import PropertyPage from './property/PropertyPage';
// --- Import Custom Hooks ---

// --- Import Custom Components ---
import { formatCurrencyForDisplay as formatCurrencyUtil } from '../utils/currencyFormatter';
import { useDevLogging } from '../hooks/useDevLogging';
import PropertyMarkersLayer from '../components/map/layers/PropertyMarkersLayer';
import StateCountBadgesLayer from '../components/map/layers/StateCountBadgesLayer'; // Import the new layer
import { BASE_URL } from '../utils/config';

// --- Fix for default marker icon ---
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

// --- React Components (Internal to Map) ---
const ChangeView = ({ center, zoom, initialCenter, initialZoom, onResetComplete }) => {
  const map = useMap();
  useEffect(() => {
    if (center === 'reset') {
      const handleMoveEnd = () => {
        if (onResetComplete) {
          onResetComplete();
        }
        map.off('moveend', handleMoveEnd); // Clean up listener
      };
      map.on('moveend', handleMoveEnd);
      // Use flyToBounds to reset the view to the same bounds as the initial load, instead of a hardcoded center/zoom.
      map.flyToBounds(US_BOUNDS, { duration: 0.8 });
      return () => {
        map.off('moveend', handleMoveEnd); // Cleanup on unmount/re-render
      };
    } else if (center && typeof center.lat === 'number' && typeof center.lon === 'number') {
      // Handle flying to a specific location
      const targetZoom = zoom || map.getZoom();
      map.flyTo([center.lat, center.lon], targetZoom, {
        duration: 1.0,
      });
    }
  }, [center, zoom, map, initialCenter, initialZoom, onResetComplete]);
  return null;
};

// Define the bounds of the contiguous US to fit the map on initial load.
const US_BOUNDS = [
  [24.396308, -124.0], // Southwest coordinates
  [49.384358, -71.05778], // Northeast coordinates (using Boston's longitude as the eastern edge)
];

// --- MapEvents Component ---
function MapEvents({ onMapIdle }) {
  const map = useMap();
  const isReady = useRef(false);

  useEffect(() => {
    if (map && !isReady.current) {
      isReady.current = true;
      map.fitBounds(US_BOUNDS, { animate: false });

      // Fix: Manually call onMapIdle to ensure bounds are set in state
      // even if fitBounds doesn't trigger a distinct move event.
      if (onMapIdle) {
        onMapIdle(map.getZoom(), map.getBounds());
      }
    }
  }, [map, onMapIdle]);

  const handleEvent = () => {
    if (isReady.current && onMapIdle) {
      onMapIdle(map.getZoom(), map.getBounds());
    }
  };

  useMapEvents({
    zoomend: handleEvent,
    moveend: handleEvent,
  });

  return null;
}

// --- Style for State Boundaries ---
const stateBoundaryStyle = {
  color: '#3388ff', // Default Leaflet blue, can be changed
  weight: 2, // Increased line weight
  opacity: 0.65,
  fillOpacity: 0.1,
};

// --- Main Map Component ---

// Reducer for managing map data loading state
const mapDataReducer = (state, action) => {
  switch (action.type) {
    case 'FETCH_START':
      return {
        ...state,
        isInitialDataLoading: true,
        isLoadingAllProperties: true,
        isLoadingFeaturedProperties: true,
        isSearching: true, // Add isSearching state
        mapError: null,
      };
    case 'FETCH_SUCCESS':
      // Calculate property counts from allProperties
      const countsMap = action.payload.allProperties.reduce((acc, prop) => {
        if (prop.state) {
          acc[prop.state] = (acc[prop.state] || 0) + 1;
        }
        return acc;
      }, {});

      return {
        ...state,
        allProperties: action.payload.allProperties,
        featuredProperties: action.payload.featuredProperties,
        statesGeoJSON: action.payload.statesGeoJSON,
        propertyCountsByState: countsMap, // ← ADD THIS
        isInitialDataLoading: false,
        isLoadingAllProperties: false,
        isSearching: false, // Update isSearching state
        isLoadingFeaturedProperties: false,
        mapError: null,
      };
    case 'FETCH_PARTIAL_ERROR':
      // Calculate counts from whichever properties we have
      const errorCountsMap = (action.payload.allProperties || state.allProperties).reduce((acc, prop) => {
        if (prop.state) {
          acc[prop.state] = (acc[prop.state] || 0) + 1;
        }
        return acc;
      }, {});

      return {
        ...state,
        allProperties: action.payload.allProperties || state.allProperties,
        featuredProperties: action.payload.featuredProperties || state.featuredProperties,
        statesGeoJSON: action.payload.statesGeoJSON || state.statesGeoJSON,
        propertyCountsByState: errorCountsMap, // ← ADD THIS
        isInitialDataLoading: false,
        isLoadingAllProperties: false,
        isSearching: false, // Update isSearching state
        isLoadingFeaturedProperties: false,
        mapError: action.payload.error,
      };
    default:
      return state;
  }
};

const initialMapDataState = {
  allProperties: [],
  featuredProperties: [],
  statesGeoJSON: null,
  propertyCountsByState: {},
  isInitialDataLoading: true,
  isLoadingAllProperties: true,
  isLoadingFeaturedProperties: true,
  isSearching: true,
  mapError: null,
};

// --- Feature Flag ---
const SHOW_MAP_STATE_COUNT = process.env.REACT_APP_SHOW_MAP_STATE_COUNT !== 'false';

// A constant empty array to ensure referential stability when no properties are returned.
const EMPTY_ARRAY = [];

const MapComponent = () => {
  // --- State Variables ---
  const [initialCenter] = useState([39.8283, -98.5795]); // Center of the contiguous US
  const [initialZoom] = useState(() => {
    // Use a wider zoom for smaller screens (mobile) to show more of the US map initially.
    const isMobile = window.innerWidth <= 768;
    return isMobile ? 4 : 5;
  });
  const [selectedStateFeature, setSelectedStateFeature] = useState(null); // State for the GeoJSON feature of the selected state
  const [selectedPropertyId, setSelectedPropertyId] = useState(null);

  const notificationSentRef = useRef(false); // Ref to track if the guest notification has been sent
  const mapRef = useRef();

  const ZOOM_THRESHOLD_FOR_BADGES = 6; // Zoom level for switching between badges and properties

  const [mapView, setMapView] = useState({
    zoom: initialZoom,

    bounds: null,
  });

  const [mapDataState, dispatchMapData] = useReducer(mapDataReducer, initialMapDataState);

  const { allProperties, featuredProperties, statesGeoJSON, propertyCountsByState, isInitialDataLoading, isLoadingAllProperties, isLoadingFeaturedProperties, isSearching, mapError } = mapDataState;

  // --- Context, Refs & Hooks ---
  const { user } = useContext(UserContext); // Get user from context
  const { addNotification } = useContext(NotificationContext);
  const [searchParams, setSearchParams] = useSearchParams();

  const lang = useLanguage();
  const { t } = useTranslation();
  const { selectedMapLocation, filters: contextFilters, selectMapLocation } = useMapContext(); // Get filters and setter from context

  // Memoize the filters object to ensure its reference is stable across re-renders.
  // This is crucial for preventing the main data-fetching useEffect from running unnecessarily.
  // By breaking down the context object into primitive values and stable strings for arrays,
  // we ensure this memo only recalculates when the filter *values* actually change.
  const typeFilterStr = contextFilters.type?.join(',');
  const statusFilterStr = contextFilters.status?.join(',');
  const filters = useMemo(
    () => contextFilters,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      contextFilters.state,
      contextFilters.price?.$gte,
      contextFilters.price?.$lte,
      contextFilters.beds?.$gte,
      contextFilters.beds?.$lte,
      contextFilters.yearBuilt?.$gte,
      contextFilters.yearBuilt?.$lte,
      contextFilters.propertyID,
      contextFilters.sellerUsername,
      typeFilterStr,
      statusFilterStr,
    ]
  );

  useDevLogging('Map.js', {
    isSearching,
    isInitialDataLoading,
    isLoadingAllProperties,
    isLoadingFeaturedProperties,
    allPropertiesCount: allProperties.length,
    featuredPropertiesCount: featuredProperties.length,
    selectedStateAbbr: filters.state,
    zoom: mapView.zoom,
    mapBoundsExists: !!mapView.bounds,
    hasGeoJSON: !!statesGeoJSON,
    propertyCountsKeys: Object.keys(propertyCountsByState).length,
  });

  // Memoize the currency formatting function so its reference is stable across re-renders.
  // This is crucial for preventing unnecessary re-renders of memoized child components like MapSidebar.
  const formatCurrencyForDisplay = useCallback(
    (value) => {
      return formatCurrencyUtil(value, lang);
    },
    [lang]
  );

  // Handler to open the property page overlay
  const handleSelectProperty = useCallback(
    (propertyId) => {
      setSelectedPropertyId(propertyId);
      // Update URL without navigating
      searchParams.set('propertyId', propertyId);
      setSearchParams(searchParams, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  // Handler to close the property page overlay
  const handleCloseProperty = useCallback(() => {
    setSelectedPropertyId(null);
    // Clean up URL
    searchParams.delete('propertyId');
    setSearchParams(searchParams, { replace: true });
  }, [searchParams, setSearchParams]);

  // --- Determine view mode ---
  const isSavedView = searchParams.get('show') === 'saved';
  const isMyPropertiesView = searchParams.get('view') === 'my';

  const isAnyFilterActive = useMemo(() => {
    // This logic should mirror the one in TopBar.js to determine if any filter is applied.
    return (
      filters.state ||
      filters.price?.$gte ||
      filters.price?.$lte ||
      filters.beds?.$gte ||
      filters.beds?.$lte ||
      (filters.type && filters.type.length > 0) ||
      filters.yearBuilt?.$gte ||
      filters.yearBuilt?.$lte ||
      (filters.status && filters.status.length > 0) ||
      filters.propertyID ||
      filters.sellerUsername ||
      isSavedView || // Treat these as active filters
      isMyPropertiesView
    );
  }, [filters, isSavedView, isMyPropertiesView]);

  const navigate = useNavigate();
  // Effect to show registration prompt to guests

  // Effect to open property page if propertyId is in the URL on load
  useEffect(() => {
    const propertyIdFromUrl = searchParams.get('propertyId');
    if (propertyIdFromUrl && !selectedPropertyId) {
      setSelectedPropertyId(propertyIdFromUrl);
    }
  }, [searchParams, selectedPropertyId]);
  useEffect(() => {
    if (!user.isLoggedIn && !notificationSentRef.current) {
      notificationSentRef.current = true; // Set the flag to true immediately
      const message = t('map.guestTip');
      addNotification(
        message,
        'info',
        3000, // 3-second duration
        false, // No beep
        false, // Not persistent
        [
          // Add two action buttons
          { text: t('map.signIn'), onClick: () => navigate(`/${lang}/login`) },
          { text: t('map.register'), onClick: () => navigate(`/${lang}/subscribe`) },
        ]
      );
    }
  }, [user.isLoggedIn, addNotification, navigate, lang, t]);

  // --- Calculate Properties in View ---
  const propertiesInView = useMemo(() => {
    if (!mapView.bounds || allProperties.length === 0) {
      return EMPTY_ARRAY;
    }
    return allProperties.filter((prop) => {
      if (prop.location?.coordinates && prop.location.coordinates.length === 2) {
        const lat = prop.location.coordinates[1];
        const lng = prop.location.coordinates[0];
        if (typeof lat === 'number' && typeof lng === 'number') {
          return mapView.bounds.contains(L.latLng(lat, lng));
        }
      }
      return false;
    });
  }, [allProperties, mapView.bounds]);

  // Determine if state count badges should be actively shown
  const actualActiveStateBadgesVisible = useMemo(() => {
    // If the feature flag is disabled, never show the badges.
    if (!SHOW_MAP_STATE_COUNT) {
      return false;
    }

    // Base condition: We need the data (GeoJSON and counts) to even consider showing badges
    const hasBadgeData = statesGeoJSON && propertyCountsByState && Object.keys(propertyCountsByState).length > 0;

    // If we are in the "Saved Homes" view, never show state badges.
    if (isSavedView || isMyPropertiesView) {
      return false;
    }

    // Badges should only show if NO specific state is selected
    if (filters.state) {
      return false;
    }

    // If no state is selected, check if we should show badges based on zoom or property count
    if (!hasBadgeData) {
      return false;
    }

    // Badges should be visible if the current zoom is at or below the threshold
    const badgesShouldShow = mapView.zoom <= ZOOM_THRESHOLD_FOR_BADGES;
    return badgesShouldShow;
  }, [isSavedView, isMyPropertiesView, statesGeoJSON, propertyCountsByState, filters.state, mapView.zoom]);

  const handleResetComplete = useCallback(() => {
    // After the map has flown back to the initial view, clear the reset signal.
    selectMapLocation(null);
  }, [selectMapLocation]);

  const sidebarTitle = useMemo(() => {
    if (isSavedView) return t('map.mySavedHomes');
    if (isMyPropertiesView) return t('map.myProperties');
    // If badges are visible (zoomed out), the title depends on whether a filter is active.
    if (actualActiveStateBadgesVisible) {
      return isAnyFilterActive ? t('map.matchingProperties') : t('map.featuredProperties');
    }

    return null; // Default title
  }, [isSavedView, isMyPropertiesView, actualActiveStateBadgesVisible, t, isAnyFilterActive]);

  const filteredFeaturedProperties = useMemo(() => {
    if (!featuredProperties || featuredProperties.length === 0) {
      return EMPTY_ARRAY;
    }

    return featuredProperties.filter((prop) => {
      // Price filter
      if (filters.price?.$gte && prop.price < filters.price.$gte) return false;
      if (filters.price?.$lte && prop.price > filters.price.$lte) return false;

      // Beds filter
      if (filters.beds?.$gte && prop.beds < filters.beds.$gte) return false;
      if (filters.beds?.$lte && prop.beds > filters.beds.$lte) return false;

      // Year Built filter
      if (filters.yearBuilt?.$gte && prop.yearBuilt < filters.yearBuilt.$gte) return false;
      if (filters.yearBuilt?.$lte && prop.yearBuilt > filters.yearBuilt.$lte) return false;

      // Type filter
      const propertyTypeString = PropertyTypeNumericToStringMap[prop.type];
      if (filters.type?.length > 0 && !filters.type.includes(propertyTypeString)) return false;

      // Status filter
      if (filters.status?.length > 0 && !filters.status.includes(prop.status)) return false;

      // State filter
      if (filters.state && prop.state !== filters.state) return false;

      // Property ID filter
      if (filters.propertyID && String(prop.propertyID) !== filters.propertyID) return false;

      // Seller Username filter
      if (filters.sellerUsername && prop.sellerUsername !== filters.sellerUsername) return false;

      return true;
    });
  }, [featuredProperties, filters]);

  // Effect to fit map bounds to saved properties when in saved view
  useEffect(() => {
    if ((isSavedView || isMyPropertiesView) && allProperties.length > 0 && mapRef.current) {
      const propertyCoordinates = allProperties
        .map((p) => {
          if (p.location?.coordinates && p.location.coordinates.length === 2) {
            // Leaflet expects [lat, lng]
            return [p.location.coordinates[1], p.location.coordinates[0]];
          }
          return null;
        })
        .filter(Boolean);

      if (propertyCoordinates.length > 0) {
        const bounds = L.latLngBounds(propertyCoordinates);
        if (bounds.isValid()) {
          mapRef.current.fitBounds(bounds, { padding: [50, 50] });
        }
      }
    }
  }, [isSavedView, isMyPropertiesView, allProperties]); // Runs when saved view is active and properties are loaded

  // --- Unified Data Fetching Effect ---
  useEffect(() => {
    const fetchAllMapData = async () => {
      dispatchMapData({ type: 'FETCH_START' });
      const showSaved = isSavedView;
      const myPropertiesView = isMyPropertiesView;

      // --- Promise for Filtered/All Properties ---
      const fetchFilteredPropertiesPromise = (async () => {
        if (showSaved && (!user.isLoggedIn || !user.savedProperties || user.savedProperties.length === 0)) {
          return []; // Exit early for saved view with no saved properties
        }
        if (myPropertiesView && !user.isSeller) {
          return []; // Exit early for my properties view if not a seller
        }

        const filterParams = new URLSearchParams();
        if (filters.state) filterParams.append('state', filters.state);
        if (filters.propertyID) filterParams.append('propertyID', filters.propertyID);
        if (filters.price?.$gte) filterParams.append('price[$gte]', filters.price.$gte);
        if (filters.price?.$lte) filterParams.append('price[$lte]', filters.price.$lte);
        if (filters.beds?.$gte) filterParams.append('beds[$gte]', filters.beds.$gte);
        if (filters.beds?.$lte) filterParams.append('beds[$lte]', filters.beds.$lte);
        if (filters.yearBuilt?.$gte) filterParams.append('yearBuilt[$gte]', filters.yearBuilt.$gte);
        if (filters.yearBuilt?.$lte) filterParams.append('yearBuilt[$lte]', filters.yearBuilt.$lte);
        filters.type?.forEach((type) => filterParams.append('type', type));
        filters.status?.forEach((status) => filterParams.append('status', status));

        if (showSaved) {
          const propertyIDs = user.savedProperties.join(',');
          filterParams.append('propertyIDs', propertyIDs);
        }

        let url;
        let fetchOptions = {};
        filterParams.append('forMap', 'true');
        const queryString = filterParams.toString();

        if (filters.propertyID) {
          url = `${BASE_URL}/api/properties?${queryString}`;
          if (user.token) fetchOptions = { headers: { Authorization: `Bearer ${user.token}` } };
        } else if (myPropertiesView) {
          url = `${BASE_URL}/api/properties/my-properties?${queryString}`;
          fetchOptions = { headers: { Authorization: `Bearer ${user.token}` } };
        } else {
          url = `${BASE_URL}/api/properties?${queryString}`;
        }

        const response = await fetchWithTimeout(url, fetchOptions, 20000);
        if (!response.ok) throw new Error(`Failed to fetch properties: ${response.statusText}`);
        const data = await response.json();
        return data.properties || [];
      })();

      // --- Promise for Featured Properties ---
      const fetchFeaturedPropertiesPromise = (async () => {
        const response = await fetchWithTimeout(`${BASE_URL}/api/properties/featured`);
        if (!response.ok) throw new Error(t('map.fetchFeaturedError'));
        return await response.json();
      })();

      // --- Promise for States GeoJSON ---
      const fetchStatesGeoJSONPromise = (async () => {
        const response = await fetchWithTimeout(`${BASE_URL}/api/states/geojson`);
        if (!response.ok) throw new Error(t('map.fetchGeoJsonError', { statusText: response.statusText }));
        return await response.json();
      })();

      // --- Execute all promises concurrently ---
      const results = await Promise.allSettled([fetchFilteredPropertiesPromise, fetchFeaturedPropertiesPromise, fetchStatesGeoJSONPromise]);

      const [filteredResult, featuredResult, geoJsonResult] = results;

      // Process all results in a single dispatch
      const allProps = filteredResult.status === 'fulfilled' ? filteredResult.value : [];
      const featProps = featuredResult.status === 'fulfilled' ? featuredResult.value || [] : [];
      const geoJson = geoJsonResult.status === 'fulfilled' ? geoJsonResult.value : null;

      // Handle errors
      let errorMessage = null;
      if (filteredResult.status === 'rejected') {
        errorMessage = filteredResult.reason.message === 'Timeout' ? t('map.loadPropertiesTimeout') : t('map.loadPropertiesError', { message: filteredResult.reason.message });
        console.error('Error fetching filtered properties:', filteredResult.reason);
      }
      if (featuredResult.status === 'rejected') {
        addNotification(t('map.loadFeaturedError'), 'warning');
        console.error('Error fetching featured properties:', featuredResult.reason);
      }
      if (geoJsonResult.status === 'rejected') {
        errorMessage = geoJsonResult.reason.message === 'Timeout' ? t('map.loadMapDataTimeout') : t('map.loadMapDataError');
        console.error('Error fetching states GeoJSON:', geoJsonResult.reason);
      }

      // Single dispatch with all data
      if (filteredResult.status === 'fulfilled' && featuredResult.status === 'fulfilled' && geoJsonResult.status === 'fulfilled') {
        dispatchMapData({
          type: 'FETCH_SUCCESS',
          payload: {
            allProperties: allProps,
            featuredProperties: featProps,
            statesGeoJSON: geoJson,
          },
        });
      } else {
        dispatchMapData({
          type: 'FETCH_PARTIAL_ERROR',
          payload: {
            allProperties: allProps,
            featuredProperties: featProps,
            statesGeoJSON: geoJson,
            error: errorMessage,
          },
        });
      }
    };

    fetchAllMapData();
  }, [filters, isMyPropertiesView, isSavedView, user.isLoggedIn, user.isSeller, user.savedProperties, user.token, addNotification, t]);

  // Effect to clear selected state when zooming out
  useEffect(() => {
    if (mapView.zoom <= ZOOM_THRESHOLD_FOR_BADGES) {
      setSelectedStateFeature(null);
    }
  }, [mapView.zoom, setSelectedStateFeature]);

  // --- Centralized function for map state transitions ---
  const flyToState = useCallback(
    (stateAbbr) => {
      if (!mapRef.current || !statesGeoJSON) {
        return; // Exit if map or data isn't ready
      }

      const calculateBounds = (feature) => {
        if (!feature?.geometry?.coordinates) return null;
        let bounds;
        if (feature.geometry.type === 'Polygon') {
          const coords = feature.geometry.coordinates[0];
          bounds = L.latLngBounds(coords.map((coord) => [coord[1], coord[0]]));
        } else if (feature.geometry.type === 'MultiPolygon') {
          bounds = L.latLngBounds([]);
          feature.geometry.coordinates.forEach((polygon) => {
            polygon[0].forEach((coord) => bounds.extend([coord[1], coord[0]]));
          });
        }
        return bounds?.isValid() ? bounds : null;
      };

      if (stateAbbr) {
        const feature = statesGeoJSON.features.find((f) => f.properties.abbreviation === stateAbbr);
        if (feature) {
          setSelectedStateFeature(feature); // Set for red highlight
          const bounds = calculateBounds(feature);
          if (bounds) {
            mapRef.current.flyToBounds(bounds, { duration: 1 });
          }
        } else {
          setSelectedStateFeature(null); // State not found, clear highlight
        }
      } else {
        // No state selected, reset view and clear highlight
        setSelectedStateFeature(null);
        mapRef.current.flyToBounds(US_BOUNDS, { duration: 1 });
      }
    },
    [statesGeoJSON, setSelectedStateFeature] // Depends on statesGeoJSON data
  );
  // --- Effects ---

  const handleStateBadgeClick = useCallback(
    (stateAbbr) => {
      flyToState(stateAbbr);
    },
    [flyToState]
  );

  // Handler for map idle (zoom or move end)
  const handleMapIdle = useCallback(
    (zoom, bounds) => {
      setMapView((prev) => {
        // Optimization: if zoom and bounds haven't changed, return the previous state to prevent a re-render.
        if (prev.zoom === zoom && prev.bounds && prev.bounds.equals(bounds)) {
          return prev;
        }
        return { zoom, bounds };
      });
    },
    [] // No dependencies, this function is stable.
  );

  // --- Determine Properties to Display ---
  const propertiesForSidebar = useMemo(() => {
    // If in saved view, always show all fetched (saved) properties in the sidebar.
    if (isSavedView || isMyPropertiesView) {
      return allProperties;
    }

    // If badges are visible (zoomed out view):
    if (actualActiveStateBadgesVisible) {
      // If any filter is active, show all properties that match the filters.
      if (isAnyFilterActive) {
        return allProperties;
      }
      // Otherwise (no filters active), show only the featured properties that match the filters.
      return filteredFeaturedProperties;
    }

    // If a specific state is selected, the sidebar should only list properties for that state.
    if (filters.state) {
      const filteredByState = allProperties.filter((prop) => prop.state === filters.state);
      return filteredByState;
    }

    // If no state is selected, the sidebar shows properties currently in the map's viewport.
    return propertiesInView; // This contains all properties in the viewport that match filters
  }, [isSavedView, isMyPropertiesView, allProperties, filters.state, propertiesInView, actualActiveStateBadgesVisible, filteredFeaturedProperties, isAnyFilterActive]);

  const propertiesForMap = useMemo(() => {
    // If in saved view, always show all fetched (saved) properties on the map.
    if (isSavedView || isMyPropertiesView) {
      return allProperties;
    }

    // If badges are visible, no markers should be shown on the map.
    if (actualActiveStateBadgesVisible) {
      return EMPTY_ARRAY;
    }
    // Otherwise, the map should always show all properties currently in the viewport.
    return propertiesInView;
  }, [isSavedView, isMyPropertiesView, allProperties, propertiesInView, actualActiveStateBadgesVisible]);

  // Effect to animate map view based on the state filter.
  useEffect(() => {
    // This effect is the single source of truth for reacting to state filter changes.
    // It calls the centralized flyToState function to handle animation and highlighting.
    flyToState(filters.state);
  }, [flyToState, filters.state]);

  // --- Key for PropertyMarkersLayer to force re-render ---
  const propertyMarkersKey = useMemo(() => {
    // This key should change if the list of all properties changes or if markersVersion is bumped.
    // We will add dependencies for highlighted properties here later.
    return `all-properties-${allProperties.length}`;
  }, [allProperties.length]);

  // --- Render ---
  return (
    <div className="map-page-container">
      {mapError && (
        <div className="map-error-overlay">
          <div className="map-error-content">
            <h3>{t('map.mapDataErrorTitle')}</h3>
            <p>{mapError}</p>
            <button onClick={() => window.location.reload()}>{t('map.refreshPage')}</button>
          </div>
        </div>
      )}
      {isInitialDataLoading && !mapError && (
        <div className="map-loading-overlay">
          <div className="map-loading-content">
            <div className="map-spinner"></div>
            <p>{t('map.loadingMapData')}</p>
          </div>
        </div>
      )}
      <div className="map-container">
        <div className="map-wrapper">
          {/* Only render the map once the essential GeoJSON data is available. */}
          {/* This prevents an initial render with the wrong zoom, followed by a second render after fitBounds. */}
          {statesGeoJSON && (
            <MapContainer
              center={initialCenter}
              zoom={initialZoom}
              bounds={US_BOUNDS} // Set initial view based on bounds, not center/zoom
              zoomSnap={0.1} // Enable fractional zoom
              zoomDelta={0.5} // Control zoom speed
              style={{ height: '100%', width: '100%' }}
              ref={mapRef}
            >
              {/* --- Base Layers & Controls --- */}
              <ChangeView center={selectedMapLocation} zoom={selectedMapLocation?.zoom} initialCenter={initialCenter} initialZoom={initialZoom} onResetComplete={handleResetComplete} />
              <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution='© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors' />
              <MapEvents onMapIdle={handleMapIdle} statesGeoJSON={statesGeoJSON} />
              {/* --- Dynamic Data Layers --- */}
              {/* Layer for the selected state highlight */}
              {selectedStateFeature && <GeoJSON data={selectedStateFeature} style={{ color: '#ff0000', weight: 2, fillOpacity: 0.3 }} />}
              {/* Property Markers Layer */}
              <PropertyMarkersLayer key={propertyMarkersKey} properties={propertiesForMap} formatCurrencyForDisplay={formatCurrencyForDisplay} isMyPropertiesView={isMyPropertiesView} onPropertySelect={handleSelectProperty} />
              {/* States GeoJSON Layer (boundaries) */}
              <GeoJSON data={statesGeoJSON} style={stateBoundaryStyle} />
              {/* State Count Badges Layer */}
              {actualActiveStateBadgesVisible && <StateCountBadgesLayer statesGeoJSON={statesGeoJSON} propertyCountsByState={propertyCountsByState} onStateBadgeClick={handleStateBadgeClick} />}
            </MapContainer>
          )}
        </div>
      </div>

      {/* --- Sidebar --- */}
      <div className="search-container">
        <MapSidebar
          // --- Loading States ---
          isSearching={isSearching} // Use combined loading state from reducer
          // --- Data ---
          propertiesToDisplay={propertiesForSidebar} // Array of properties (from polygon, neighborhood, or radius)
          // --- Context/State Information ---
          title={sidebarTitle} // Pass the new title prop
          selectedStateAbbr={filters.state} // Pass the selected state
          // --- Functions/Handlers ---
          formatCurrencyForDisplay={formatCurrencyForDisplay} // Utility function for formatting currency
          onPropertySelect={handleSelectProperty}
        />
      </div>
      {/* --- Property Page Overlay --- */}
      {selectedPropertyId && (
        <div className="property-page-overlay-container">
          <PropertyPage
            propertyIdForPage={selectedPropertyId} // Pass the ID
            onClose={handleCloseProperty} // Pass the close handler
            isPreviewMode={false} // This is not a preview in the form sense
          />
        </div>
      )}
    </div>
  );
};

export default MapComponent;
